// src/components/CalendarPicker/CalendarPicker.jsx
import React, { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import "./Calendar.css";
import { toISODate } from "../../utils/formatTime";



export default function CalendarPicker({
  value,                 // ISO date string e.g. "2025-08-10"
  onChange,              // (iso: string) => void
  minDateISO,            // optional ISO string to disable past dates
  className = "",
  startOnSunday = true,  // keep Sunday-first like your UI
}) {
  const todayISO = useMemo(() => toISODate(new Date()), []);
  const minISO = minDateISO || todayISO;

  // initial month derived from value or today
  const initialMonth = useMemo(() => {
    const base = value ? new Date(value) : new Date();
    base.setDate(1);
    base.setHours(0, 0, 0, 0);
    return base;
  }, [value]);

  const [calMonth, setCalMonth] = useState(initialMonth);

  const monthLabel = useMemo(
    () => calMonth.toLocaleString(undefined, { month: "long", year: "numeric" }),
    [calMonth]
  );

  // build cells for current month
  const cells = useMemo(() => {
    const firstDay = new Date(calMonth);
    const month = firstDay.getMonth();
    const daysInMonth = new Date(firstDay.getFullYear(), month + 1, 0).getDate();

    const startIdx = firstDay.getDay(); // 0..6 (Sun=0)
    const leading = startOnSunday ? startIdx : (startIdx + 6) % 7;

    const list = Array.from({ length: leading }, () => null);
    for (let d = 1; d <= daysInMonth; d++) list.push(d);
    return list;
  }, [calMonth, startOnSunday]);

  return (
    <div className={`calendar cal-panel ${className}`}>
      <div className="cal-header">
        <button
          className="cal-nav"
          onClick={() => {
            const d = new Date(calMonth);
            d.setMonth(d.getMonth() - 1);
            setCalMonth(d);
          }}
          aria-label="Previous month"
        >
          <ChevronLeft size={22} />
        </button>

        <div className="cal-title">{monthLabel}</div>

        <button
          className="cal-nav"
          onClick={() => {
            const d = new Date(calMonth);
            d.setMonth(d.getMonth() + 1);
            setCalMonth(d);
          }}
          aria-label="Next month"
        >
          <ChevronRight size={22} />
        </button>
      </div>

      <div className="cal-weekdays">
        {(startOnSunday
          ? ["SUN","MON","TUE","WED","THU","FRI","SAT"]
          : ["MON","TUE","WED","THU","FRI","SAT","SUN"]
        ).map((d) => (
          <div key={d} className="cal-weekday">{d}</div>
        ))}
      </div>

      <div className="cal-grid" role="grid" aria-label="Calendar">
        {cells.map((day, i) => {
          if (!day) return <div key={`b${i}`} className="cal-cell blank" />;

          const iso = toISODate(new Date(calMonth.getFullYear(), calMonth.getMonth(), day));
          const disabled = iso < minISO;
          const selected = value === iso;

          return (
            <button
              key={iso}
              type="button"
              className={`cal-cell day ${selected ? "selected" : ""}`}
              onClick={() => !disabled && onChange(iso)}
              disabled={disabled}
              aria-pressed={selected}
            >
              {day}
            </button>
          );
        })}
      </div>
    </div>
  );
}
